import type { WorkflowNodeInput } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { formatTemplateToken, splitTemplate } from '@cotera/utilities';

const { readMapperFields } = PersistedWorkflow;

type MapperNodeData = Extract<WorkflowNodeInput, { type: 'mapper' }>['data'];

export type MappingRow = {
  readonly id: string;
  readonly key: string;
  /**
   * Always the TEMPLATE form (`{{ref}}`), which is what the editor's token
   * input reads and writes. A stored whole-value ref is wrapped on the way in
   * and unwrapped again on the way out — see {@link storedValueForTemplate}.
   */
  readonly value: string;
};

/**
 * What gets persisted for a row.
 *
 * A template holding exactly one variable and nothing else is stored as the
 * BARE ref, which the runner resolves whole — so mapping an object or a list
 * keeps it an object or a list. Anything with literal text around it (or more
 * than one variable) is a string being composed, and is stored as the template.
 * This is why the editor needs no "value vs text" mode toggle: what the user
 * typed already says which one they meant.
 */
function storedValueForTemplate(template: string): string {
  const segments = splitTemplate(template);
  const only = segments.length === 1 ? segments[0] : undefined;
  if (only !== undefined && only.t === 'ref') {
    return only.ref;
  }
  return template;
}

/** The inverse: a stored bare ref shows in the editor as a variable pill. */
function templateForStoredValue(value: string): string {
  return splitTemplate(value).some((segment) => segment.t === 'ref')
    ? value
    : formatTemplateToken(value);
}

/** The editor rows for a stored mapper, in authored (`fieldOrder`) order. */
export function readMappingRows(data: Record<string, unknown> | null): {
  rows: MappingRow[];
  nextId: number;
} {
  const fields = readMapperFields(data ?? {});
  return {
    rows: fields.map((field, index) => ({
      id: `row-${index + 1}`,
      key: field.key,
      value: templateForStoredValue(field.ref),
    })),
    nextId: fields.length + 1,
  };
}

/** The mapper `data` the editor persists for its current state. */
export function buildMapperNodeData(params: {
  readonly name: string;
  readonly output: PersistedWorkflow.MapperOutputMode;
  readonly rowKey: string;
  readonly rows: readonly MappingRow[];
}): MapperNodeData {
  const { output } = params;
  const fieldBindings: Record<string, string> = {};
  let position = 0;
  for (const row of params.rows) {
    const value = row.value.trim();
    if (value === '') {
      continue;
    }
    position += 1;
    // A list's values are named by position only; the keys never reach its
    // output, so the editor doesn't ask for them.
    const key = output === 'row' ? `value_${position}` : row.key.trim();
    if (key === '') {
      continue;
    }
    fieldBindings[key] = storedValueForTemplate(value);
  }
  // Stored as JSONB, `fieldBindings` loses its key order; this keeps it.
  const fieldOrder = Object.keys(fieldBindings);
  const name = params.name.trim();
  const rowKey = params.rowKey.trim();
  return {
    ...(name !== '' ? { name } : {}),
    ...(fieldOrder.length > 0 ? { fieldBindings, fieldOrder } : {}),
    ...(output === 'row'
      ? { output, ...(rowKey !== '' ? { rowKey } : {}) }
      : {}),
  };
}
